import React, { useState, useMemo, useRef, useEffect } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import { format, isValid, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import TanstackTable from '../../Common/TanstackTable/TanstackTable';
import api from '../../../middleware/api';
import API from '../../../routes/APIEndpoints';
import Pagination from '../../Common/DaisyTable/Pagination/Pagination';
import LoadingSpinner from '../../Common/Loading/LoadingSpinner';
import CustomerDetails from './CustomerDetails';

const fetchCustomers = async () => {
    const response = await api.get(API.ADMIN_ALL_CUSTOMERS);
    if (response.status === 200) {
        return response.data.values;
    }
    return [];
};

const CustomersManagement = () => {
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [sorting, setSorting] = useState([]);
    const [tableContext, setTableContext] = useState(null);

    // Pagination state
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 50,
    });

    // Query for fetching all customers
    const {
        data,
        error,
        isLoading,
        isError,
        refetch,
        isFetching
    } = useQuery({
        queryKey: ['customers'],
        queryFn: fetchCustomers
    });

    // Filtrer d'abord les données en fonction de la recherche
    const filteredData = useMemo(() => {
        if (!data) return [];

        if (!searchQuery) return data;

        return data.filter(customer => {
            const searchLower = searchQuery.toLowerCase();
            return (
                (customer.firstname && customer.firstname.toLowerCase().includes(searchLower)) ||
                (customer.lastname && customer.lastname.toLowerCase().includes(searchLower)) ||
                (customer.email && customer.email.toLowerCase().includes(searchLower)) ||
                (customer.phone && customer.phone.toLowerCase().includes(searchLower)) ||
                (customer.id && customer.id.toString().includes(searchLower))
            );
        });
    }, [data, searchQuery]);

    // Appliquer le tri aux données filtrées
    const sortedData = useMemo(() => {
        if (!filteredData || !sorting.length) return filteredData;

        const sorted = [...filteredData];
        
        return sorted.sort((a, b) => {
            for (const sort of sorting) {
                const { id, desc } = sort;
                
                let valueA, valueB;
                
                // Gérer les différents types de colonnes
                if (id === 'id') {
                    valueA = a[id];
                    valueB = b[id];
                } else if (id === 'firstname' || id === 'lastname' || id === 'email' || id === 'phone') {
                    valueA = a[id]?.toLowerCase() || '';
                    valueB = b[id]?.toLowerCase() || '';
                } else if (id === 'creationDate') {
                    valueA = new Date(a[id] || a.modifiedDate || 0).getTime();
                    valueB = new Date(b[id] || b.modifiedDate || 0).getTime();
                } else {
                    valueA = a[id];
                    valueB = b[id];
                }
                
                // Comparaison
                if (valueA < valueB) {
                    return desc ? 1 : -1;
                }
                if (valueA > valueB) {
                    return desc ? -1 : 1;
                }
            }
            return 0;
        });
    }, [filteredData, sorting]);

    // Calculer ensuite la page courante à partir des données triées
    const currentPageData = useMemo(() => {
        if (!sortedData) return [];
        const start = pagination.pageIndex * pagination.pageSize;
        const end = start + pagination.pageSize;
        return sortedData.slice(start, end);
    }, [sortedData, pagination]);

    // Calculer le nombre de pages à partir des données triées
    const pageCount = useMemo(() => {
        if (!sortedData) return 0;
        return Math.ceil(sortedData.length / pagination.pageSize);
    }, [sortedData, pagination.pageSize]);

    // Réinitialiser la page à 0 quand le filtre change
    useEffect(() => {
        setPagination(prev => ({ ...prev, pageIndex: 0 }));
    }, [searchQuery]);

    // Create a mock table object for the Pagination component
    const mockTable = useMemo(() => ({
        getPageCount: () => pageCount,
        setPageIndex: (index) => {
            setPagination(prev => ({ ...prev, pageIndex: index }));
        },
        setPageSize: (size) => setPagination(prev => ({ ...prev, pageSize: size, pageIndex: 0 })),
        nextPage: () => setPagination(prev => ({ ...prev, pageIndex: Math.min(prev.pageIndex + 1, pageCount - 1) })),
        previousPage: () => setPagination(prev => ({ ...prev, pageIndex: Math.max(prev.pageIndex - 1, 0) })),
        getRowCount: () => sortedData?.length || 0
    }), [pageCount, sortedData]);

    const handleFetchAll = () => {
        console.log("All data is already fetched");
    };

    const handleOpenDetails = (customer = null) => {
        setSelectedCustomer(customer);
        setIsDetailsModalOpen(true);
    };

    const handleCloseDetails = () => {
        setIsDetailsModalOpen(false);
        setSelectedCustomer(null);
    };

    const tableRef = useRef(null);

    // Effect to scroll to top when pagination changes
    useEffect(() => {
        if (tableRef.current) {
            const parent = tableRef.current.parentElement;
            parent.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [pagination.pageIndex]);

    if (isError) {
        return <div className="alert alert-error">Une erreur est survenue : {error.message}</div>;
    }

    if (isLoading) {
        return <LoadingSpinner style="medium"/>;
    }

    const columns = [
        {
            header: 'ID',
            accessorKey: 'id',
            sortingFn: 'basic',
        },
        {
            header: 'Prénom',
            accessorKey: 'firstname',
            sortingFn: 'text',
        },
        {
            header: 'Nom',
            accessorKey: 'lastname',
            sortingFn: 'text',
        },
        {
            header: 'Email',
            accessorKey: 'email',
            sortingFn: 'text',
        },
        {
            header: 'Téléphone',
            accessorKey: 'phone',
            sortingFn: 'text',
        },
        {
            header: 'Date de création',
            accessorKey: 'creationDate',
            cell: ({ row }) => {
                const dateValue = row.original.creationDate ? row.original.creationDate : row.original.modifiedDate ? row.original.modifiedDate : null;
                if (!dateValue) return 'Aucune date';

                const date = parseISO(dateValue);
                return isValid(date) ? format(date, 'dd/MM/yyyy', { locale: fr }) : 'Date invalide';
            },
            sortingFn: 'datetime',
        },
        {
            header: 'Actions',
            accessorKey: 'actions',
            enableSorting: false,
            cell: ({ row }) => (
                <button
                    className="btn btn-sm btn-primary"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleOpenDetails(row.original);
                    }}
                >
                    Détails
                </button>
            ),
        },
    ];

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold">Gestion des Clients</h2>
                <div className="flex items-center gap-2">
                    <button
                        className="btn btn-outline"
                        onClick={() => refetch()}
                        disabled={isFetching}
                    >
                        {isFetching ? 'Actualisation...' : 'Actualiser'}
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={() => handleOpenDetails()}
                    >
                        <PlusIcon className="w-5 h-5 mr-2" />
                        Ajouter un client
                    </button>
                </div>
            </div>

            <div className="flex justify-between items-center">
                <input
                    type="text"
                    placeholder="Rechercher par Id, Nom, Prénom, Email ou Téléphone"
                    className="input input-md input-primary input-bordered w-full max-w-xs"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />
                <div className='flex flex-row gap-2'>
                    <Pagination
                        server={{
                            total: sortedData?.length || 0,
                            fetched: sortedData?.length || 0,
                        }}
                        client={pagination}
                        table={tableContext || mockTable}
                        handleFetchAll={handleFetchAll}
                    />

                    <p className="text-xs text-gray-600 self-end">{sortedData.length} résultats</p>
                </div>
            </div>

            <div className="bg-white rounded-lg shadow p-4" ref={tableRef}>
                <TanstackTable
                    data={currentPageData}
                    columns={columns}
                    onRowClick={(row) => handleOpenDetails(row.original)}
                    sorting={sorting}
                    setSorting={setSorting}
                />

                <div className="mt-4 flex justify-end">
                    <Pagination
                        server={{
                            total: sortedData?.length || 0,
                            fetched: sortedData?.length || 0,
                        }}
                        client={pagination}
                        table={tableContext || mockTable}
                        handleFetchAll={handleFetchAll}
                    />
                </div>
            </div>

            <CustomerDetails
                isOpen={isDetailsModalOpen}
                onClose={handleCloseDetails}
                customer={selectedCustomer}
            />
        </div>
    );
};

export default CustomersManagement;
